import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Container } from '../../Layouts/Container';
import { DashboardLayout } from '../../Layouts/DashboardLayout';
import { Wrapper } from '../../Layouts/Wrapper';
import * as Icons from "../../components/Icons";
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { Table, TableHead } from '../../components/Table';
import { Pagination } from '../../components/Pagination';
import {
    formatDate,
    getNotificationsGeneralList,
    getNotificationsGeneralTotal,
    OS,
    getSearchParams,
    getNotificationGeneralPDF,
    getNotificationGeneralSurety
} from '../../utils';
import { NotificationCard } from '../../components/Card';
import { RootState } from '../../store';
import Loading from '../../components/Loading';
import { Empty } from '../../components/Empty';
import { initializeRows, initializeTotal, changePage as changePageAction } from '../../store/notificationsSlice';
import { Tooltip } from '../../components/Tooltip';
import * as BridgeActions from '../../utils/bridge_actions';
import { useNavigate } from '../../hook';

export const path: string = 'notifications-general';

const renderRowPDF = (rowData: any, params: any) => {
    return (
        <div className={styles.ActionGroup}>
            <Tooltip
                title={params.title}
                type='info'
                display='inline'
                followTheCursor
            >
                <span onClick={() => params?.downloadPDF?.(rowData)} className='action-item'>
                    <Icons.PDF />
                </span>
            </Tooltip>
        </div>
    );
};

const renderRowSurety = (rowData: any, params: any) => {
    return (
        <div className={styles.ActionGroup}>
            {rowData.showReceipt ? (
                <Tooltip
                    title={params.title}
                    type='info'
                    display='inline'
                    followTheCursor
                >
                <span onClick={() => params?.downloadSuretyPDF?.(rowData)} className='action-item'>
                    <Icons.PDF />
                </span>
                </Tooltip>
            ) : null}
        </div>
    );
};

const NotificationsGeneralPage = () => {
    const { t } = useTranslation();
    const notifications = useSelector((state: RootState) => state.notifications);
    const dispatch = useDispatch();
    const navigation = useNavigate();
    const auth: any = useSelector((state: RootState) => state.auth);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [firstOpen, setFirstOpen] = useState<boolean>(true);
    const [reset, setReset] = useState(false);
    const utils = useSelector((state: any) => state.utils);

    const onSearch = async (page?: number) => {
        setIsLoading(true);
        try {
            const filter = {};
            const filterForList = {
                ...filter,
                currentPage: page || notifications.currentPage,
                pageSize: notifications.pageSize
            }
            const filterForTotal = { ...filter };
            const [list, total] = await Promise.all([
                getNotificationsGeneralList(filterForList),
                getNotificationsGeneralTotal(filterForTotal)
            ]);
            dispatch(initializeRows(list.data));
            dispatch(initializeTotal(total.data.value || 0));
            setFirstOpen(false);
        } catch (err: any) {
            console.log('onSearch >>>', err);
            toast.error(t(err.message), { position: 'top-right', theme: 'colored' });
        } finally {
            setIsLoading(false);
        }
    }

    const resetState = () => {
        dispatch(initializeRows([]));
        dispatch(initializeTotal(0));
    }

    const changePage = (page: number) => {
        dispatch(changePageAction(page));
        onSearch(page);
    }

    const downloadPDF = (rowData: any) => {
        const { mobileView, os} = getSearchParams();
        if(!!mobileView && os === OS.android) {
            return BridgeActions.onAction({...BridgeActions.DOWNOALD_PDF, payload: { apiPath: `/api/notification/notificationPdf/${rowData.dln}/${rowData.baseType}` }});
        }
        getNotificationGeneralPDF(rowData.dln, rowData.baseType)
            .then((res: any) => {
                const fileName = res.headers['content-disposition'].replace('attachment; filename=', '').replace(/"/g, '');
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(err => {
                console.log('downloadPDF >>>', err);
                toast.error(err.message, { position: 'top-right', theme: 'colored' });
            });
    }

    const downloadSuretyPDF = (rowData: any) => {
        const { mobileView, os} = getSearchParams();
        if(!!mobileView && os === OS.android) {
            return BridgeActions.onAction({...BridgeActions.DOWNOALD_PDF, payload: { apiPath: `/api/notification/notificationSurety/${rowData.dln}` }});
        }
        getNotificationGeneralSurety(rowData.dln)
            .then((res: any) => {
                const fileName = res.headers['content-disposition'].replace('attachment; filename=', '').replace(/"/g, '');
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(err => {
                console.log('downloadPDF >>>', err);
                toast.error(err.message, { position: 'top-right', theme: 'colored' });
            });
    }

    useEffect(() => {
        if (reset) {
            changePage(1);
            setReset(false);
        }
    }, [reset]);

    useEffect(() => {
        if (!auth?.tin || (auth?.tin && !utils.tinIsValid)) {
            navigation('/dashboard');
        }
    }, [auth?.tin, utils.tinIsValid]);

    useEffect(() => {
        onSearch();
        return () => resetState();
    }, []);

    const tableHead: TableHead[] = useMemo(() => ([
        { key: "notificationName", title: t("NOTIFICATIONS_NAME"), className: 'fixTdCol400' },
        { key: "notificationDate", title: t("NOTIFICATIONS_DATE") },
        { key: "notify", title: t("NOTIFICATIONS_NOTIFY"), colType: "action", actionRow: renderRowPDF, colActionArgs: { downloadPDF, title: t("NOTIFICATIONS_NOTIFY") }, className: 'fixTdCol50' },
        { key: "surety", title: t("NOTIFICATIONS_SURETY"), colType: "action", actionRow: renderRowSurety, colActionArgs: { downloadSuretyPDF, title: t("NOTIFICATIONS_SURETY") }, className: 'fixTdCol50' },
    ]), [t, notifications.rows]);

    let listView = null;
    const ROWS = notifications.rows.map(row => ({
        ...row,
        notificationName: row.baseName,
        notificationDate: formatDate('DD.MM.YYYY', new Date(row.notifyDate))
    }));
    if (utils.device.mode === 'desktop') {
        listView = (
            <Table
                data={ROWS}
                theads={tableHead}
                verticalSize='middle'
            />
        );
    } else {
        listView = ROWS.length ? (
            <>
                {ROWS.map((row: any, index:number) => (
                    <NotificationCard
                        key={index}
                        title={row.baseName}
                        notifyDate={formatDate('DD.MM.YYYY', new Date(row.notifyDate))}
                        downloadDocument={() => downloadPDF(row)}
                        downloadSuretyDocument={row.showReceipt ? () => downloadSuretyPDF(row) : null}
                    />
                ))}
            </>
        ) : <Empty title={t("dataNotFound")} />
    }

    return (
        <DashboardLayout className={styles.Notifications}>
            <Container containerSize="large" className={styles.NotificationsContainer} withoutPadding>
                <Wrapper title={t("notificationsGeneral")}>
                    <div className={styles.TableSection}>
                        {firstOpen && !ROWS.length ? null : listView}
                        <Pagination pageSize={notifications.pageSize} total={notifications.total} currentPage={notifications.currentPage} onChange={changePage} />
                    </div>
                </Wrapper>
                {isLoading && <Loading />}
            </Container>
        </DashboardLayout>
    );
};
export default NotificationsGeneralPage;